import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';

// En la Mac puede apuntar al emulador (REACT_APP_FUNCTIONS_URL, ver README "Probar en local").
const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-transferisok.cloudfunctions.net';

const HelpModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        tipoConsulta: '',
        mensaje: ''
    });
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [error, setError] = useState(null);

    const tiposConsulta = [
        'Consulta',
        'Sugerencia',
        'Tengo un problema'
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleTipoConsultaClick = (tipo) => {
        setFormData(prevState => ({
            ...prevState,
            tipoConsulta: tipo
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Añadir un timeout para evitar que la petición quede colgada indefinidamente
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 segundos de timeout

        try {
            const usuario = getAuth().currentUser;
            if (!usuario) {
                throw new Error('Usuario no autenticado');
            }

            // El email y la cuenta los toma la función de la sesión: acá solo van el
            // motivo y el mensaje.
            const response = await fetch(`${FUNCTIONS_URL}/enviarConsulta`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await usuario.getIdToken()}`
                },
                body: JSON.stringify({
                    tipoConsulta: formData.tipoConsulta,
                    mensaje: formData.mensaje
                }),
                signal: controller.signal
            });

            const responseData = await response.json().catch(() => ({}));
            if (!response.ok || !responseData.success) {
                throw new Error(responseData.message || 'Error al enviar la consulta');
            }

            // Limpiar el formulario y mostrar mensaje de éxito
            setFormData({
                tipoConsulta: '',
                mensaje: ''
            });
            setSuccessMessage('Tu consulta ha sido enviada exitosamente');

            // Cerrar el modal después de 3 segundos
            setTimeout(() => {
                setSuccessMessage(null);
                onClose();
            }, 3000);
        } catch (error) {
            console.error('No se pudo enviar la consulta:', error.name === 'AbortError' ? 'timeout' : error.message);
            setError('Ocurrió un error al enviar tu consulta. Por favor, intenta nuevamente.');
        } finally {
            clearTimeout(timeoutId);
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
        }}>
            <div className="modal-content" style={{ maxWidth: '500px', margin: '0 auto' }}>
                <div className="modal-header">
                    <h2>Centro de Ayuda</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                {successMessage && (
                    <div className={`success-message ${successMessage ? '' : 'fade-out'}`}>
                        {successMessage}
                    </div>
                )}

                {error && (
                    <div className={`error-message ${error ? '' : 'fade-out'}`}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="help-form">
                    <div className="form-group">
                        <label htmlFor="tipoConsulta"></label>
                        <div className="tipo-consulta-cards">
                            {tiposConsulta.map(tipo => (
                                <div
                                    key={tipo}
                                    className={`tipo-consulta-card ${formData.tipoConsulta === tipo ? 'selected' : ''}`}
                                    onClick={() => handleTipoConsultaClick(tipo)}
                                >
                                    {tipo}
                                </div>
                            ))}
                        </div>
                        {formData.tipoConsulta === '' && (
                            <div className="validation-message">Selecciona el motivo del mensaje</div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="mensaje">Mensaje</label>
                        <textarea
                            id="mensaje"
                            name="mensaje"
                            value={formData.mensaje}
                            onChange={handleInputChange}
                            maxLength={3000}
                            rows={6}
                            required
                            placeholder="Escribe tu mensaje aquí..."
                        />
                        <div className="character-count">
                            {formData.mensaje.length}/3000 caracteres
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="submit-button"
                        disabled={loading || formData.tipoConsulta === ''}
                    >
                        {loading ? 'Enviando...' : 'Enviar consulta'}
                    </button>
                </form>
            </div>

            <style jsx>{`
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }
                
                .modal-content {
                    background-color: white;
                    border-radius: 8px;
                    padding: 20px;
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                    max-height: 90vh;
                    overflow-y: auto;
                }
                
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #eee;
                }
                
                .close-button {
                    background: transparent;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #666;
                }
                
                .help-form {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                
                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                
                .form-group label {
                    font-weight: 500;
                    color: #333;
                }
                
                .tipo-consulta-cards {
                    display: flex;
                    gap: 10px;
                    flex-wrap: nowrap;
                    margin-bottom: 10px;
                    width: 100%;
                }
                
                .tipo-consulta-card {
                    padding: 8px 10px;
                    border: 2px solid #ddd;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    text-align: center;
                    flex: 1;
                    font-size: 14px;
                    white-space: nowrap;
                }
                
                .tipo-consulta-card:hover {
                    border-color: #007bff;
                    background-color: #f0f7ff;
                }
                
                .tipo-consulta-card.selected {
                    border-color: #002052;
                    background-color: #002052;
                    color: white;
                }
                
                textarea {
                    padding: 10px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    resize: vertical;
                    font-family: inherit;
                }
                
                .character-count {
                    text-align: right;
                    font-size: 0.8rem;
                    color: #666;
                }
                
                .submit-button {
                    padding: 12px;
                    background-color: #002052;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-weight: 500;
                    margin-top: 10px;
                    transition: background-color 0.2s;
                }
                
                .submit-button:hover:not(:disabled) {
                    background-color: #001538;
                }
                
                .submit-button:disabled {
                    background-color: #cccccc;
                    cursor: not-allowed;
                }
                
                .success-message {
                    background-color: #d4edda;
                    color: #155724;
                    padding: 10px 15px;
                    border-radius: 4px;
                    margin-bottom: 15px;
                    transition: opacity 0.5s ease-out;
                }
                
                .error-message {
                    background-color: #f8d7da;
                    color: #721c24;
                    padding: 10px 15px;
                    border-radius: 4px;
                    margin-bottom: 15px;
                    transition: opacity 0.5s ease-out;
                }
                
                .fade-out {
                    opacity: 0;
                }
                
                .validation-message {
                    font-size: 0.8rem;
                    color: #dc3545;
                    text-align: center;
                    margin-top: 5px;
                }
            `}</style>
        </div>
    );
};

export default HelpModal; 